export type Category = 'Sales' | 'Communication' | 'Documents' | 'Business' | 'Marketing' | 'Productivity'

export interface Product {
  slug: string
  name: string
  initials: string
  category: Category
  tagline: string
  description: string
  score: number
  reviews: number
  pricing: string
  bestFor: string
  accent: string
  logoBackground: string
  featured?: boolean
  editorsChoice?: boolean
  partnerUrl: string
  pros: string[]
  cons: string[]
  verdict: string
  updated: string
  summary: string[]
  keyFeatures: { title: string; description: string }[]
  alternatives: string[]
  notFor: string
}

export interface EditorialArticle {
  title: string
  excerpt: string
  category: string
  readTime: string
  date: string
  accent: string
}

export const products: Product[] = [
  {
    slug: 'apollo-io-review',
    name: 'Apollo.io',
    initials: 'A',
    category: 'Sales',
    tagline: 'The complete sales intelligence platform',
    description: 'A deeply capable prospecting and engagement platform that gives revenue teams accurate B2B data, sequencing tools, and practical workflow automation in one place.',
    score: 4.8,
    reviews: 7421,
    pricing: 'Free plan available · See current plans',
    bestFor: 'B2B sales teams building repeatable outbound engines',
    accent: '#5b55e7',
    logoBackground: '#eceaff',
    featured: true,
    editorsChoice: true,
    partnerUrl: 'https://www.apollo.io/',
    pros: ['Large, filterable B2B contact database', 'Sequences, dialer, enrichment, and workflows in one platform', 'Useful free plan for evaluating the core workflow'],
    cons: ['Credit rules and plan limits require careful review', 'Data accuracy varies by market and should be verified'],
    verdict: 'Apollo is a strong all-in-one choice for B2B teams that want prospecting data and multichannel outreach in one workspace.',
    updated: 'September 2, 2026',
    summary: ['Apollo.io combines a B2B contact database with sales engagement tools. Users can build prospect lists, enrich records, create email sequences, make calls, and sync activity with popular CRMs.', 'Its main advantage is consolidation: a growing sales team can replace separate data, sequencing, and enrichment products. The trade-off is complexity. Teams should define credit usage, data verification, and sending-domain safeguards before scaling campaigns.', 'Apollo works best as a prospecting system, not as a substitute for thoughtful targeting. Strong results still depend on relevant segmentation, verified contact data, and personalized messaging.'],
    keyFeatures: [{ title: 'People and company search', description: 'Build lists with filters for role, seniority, company size, location, technology, and buying signals.' }, { title: 'Sales engagement', description: 'Create multistep email, call, and task sequences while tracking activity in a shared workspace.' }, { title: 'Enrichment and workflows', description: 'Fill missing CRM fields and automate routine actions when records match defined criteria.' }, { title: 'CRM integrations', description: 'Connect common CRM platforms so prospect and engagement data can move between systems.' }],
    alternatives: ['ZoomInfo', 'Cognism', 'Lusha'],
    notFor: 'Teams that only need a lightweight email sender or require guaranteed accuracy for every contact record.',
  },
  {
    slug: 'quo-review',
    name: 'Quo',
    initials: 'Q',
    category: 'Communication',
    tagline: 'A smarter business phone for modern teams',
    description: 'A polished cloud phone system that makes shared numbers, call routing, texting, and customer conversations refreshingly simple for small businesses.',
    score: 4.7,
    reviews: 1934,
    pricing: 'Multiple plans available · See current plans',
    bestFor: 'Small teams that need a flexible shared business number',
    accent: '#135f55',
    logoBackground: '#dff4ec',
    featured: true,
    partnerUrl: 'https://get.quo.com/brlstrjkjvmq',
    pros: ['Fast setup across desktop and mobile', 'Shared numbers and internal collaboration are easy to understand', 'Calling and business texting live in one app'],
    cons: ['Advanced contact-center reporting is limited', 'International availability and messaging rules vary'],
    verdict: 'Quo is a practical business phone system for small teams that want shared calling and texting without enterprise complexity.',
    updated: 'August 28, 2026',
    summary: ['Quo, formerly known as OpenPhone, is a cloud business phone system built around shared phone numbers. Teams can call, text, assign conversations, leave internal notes, and manage customer communication from desktop and mobile apps.', 'The product is notably easier to adopt than a traditional PBX or contact-center suite. Shared history helps teammates understand a customer relationship before responding, while routing and business-hours controls keep a small operation organized.', 'Quo is best evaluated as a collaborative phone system. Companies needing workforce management, complex call-center analytics, or highly customized enterprise routing may need a more specialized platform.'],
    keyFeatures: [{ title: 'Shared business numbers', description: 'Let multiple teammates work from one number with shared context and clear ownership.' }, { title: 'Calling and messaging', description: 'Handle business calls, voicemail, and SMS or MMS conversations from supported devices.' }, { title: 'Call routing', description: 'Set business hours, ring orders, menus, and forwarding rules for inbound calls.' }, { title: 'Conversation collaboration', description: 'Use internal comments, mentions, and shared history without exposing notes to customers.' }],
    alternatives: ['RingCentral', 'Dialpad', 'Grasshopper'],
    notFor: 'Large contact centers that need deep workforce management, quality assurance, and enterprise analytics.',
  },
  {
    slug: 'foxit-review',
    name: 'Foxit',
    initials: 'F',
    category: 'Documents',
    tagline: 'Powerful PDF work without the heavyweight feel',
    description: 'A fast and cost-conscious PDF editor with mature collaboration, e-signature, security, and AI-assisted document features for individuals and businesses.',
    score: 4.6,
    reviews: 5082,
    pricing: 'Subscription options available · See current plans',
    bestFor: 'Teams seeking a capable Adobe Acrobat alternative',
    accent: '#ef672d',
    logoBackground: '#ffeadf',
    featured: true,
    partnerUrl: 'https://www.foxit.com/',
    pros: ['Fast desktop performance for everyday PDF work', 'Broad editing, conversion, signing, and security toolkit', 'Available across desktop, web, and mobile products'],
    cons: ['Product lineup and license options can be confusing', 'Some AI and e-sign capabilities may be separately limited'],
    verdict: 'Foxit is a capable Acrobat alternative for people who regularly edit, convert, secure, and sign PDF documents.',
    updated: 'August 22, 2026',
    summary: ['Foxit offers a family of PDF products covering reading, editing, conversion, electronic signatures, document administration, and developer services. Its PDF Editor is the central option for knowledge workers who need more than viewing and annotation.', 'Common workflows—including changing text, rearranging pages, creating forms, redacting sensitive information, and converting Office files—are presented in a familiar ribbon-style interface. This makes Foxit approachable for former Acrobat users, although the number of controls can feel busy.', 'Buyers should compare editions carefully because desktop editing, cloud services, AI allowances, and e-signature usage can have different limits.'],
    keyFeatures: [{ title: 'PDF editing', description: 'Edit text and objects, organize pages, add comments, and create interactive forms.' }, { title: 'Conversion and OCR', description: 'Convert common document formats and make scanned pages searchable with optical character recognition.' }, { title: 'Security tools', description: 'Apply passwords, permissions, redaction, certificates, and other document protections.' }, { title: 'Electronic signatures', description: 'Prepare and send documents for signature through Foxit’s connected e-sign tools.' }],
    alternatives: ['Adobe Acrobat', 'PDF-XChange Editor', 'Nitro PDF'],
    notFor: 'Occasional users who only need to read, highlight, or sign a small number of PDFs.',
  },
  {
    slug: 'ownr-review',
    name: 'Ownr',
    initials: 'O',
    category: 'Business',
    tagline: 'Start and manage a Canadian business',
    description: 'An approachable business formation service that guides Canadian founders through registration, incorporation, documents, and recurring compliance tasks.',
    score: 4.7,
    reviews: 1128,
    pricing: 'Package and government fees vary · See current plans',
    bestFor: 'Canadian entrepreneurs launching their first company',
    accent: '#d1493f',
    logoBackground: '#ffe8e4',
    featured: true,
    partnerUrl: 'https://www.ownr.co/',
    pros: ['Guided Canadian registration and incorporation flow', 'Central place for company documents and ongoing tasks', 'Plain-language experience for first-time founders'],
    cons: ['Focused on Canadian businesses', 'Government fees and availability differ by province'],
    verdict: 'Ownr makes Canadian business formation easier to navigate, especially for founders who prefer guided digital steps.',
    updated: 'August 16, 2026',
    summary: ['Ownr is an online platform for registering or incorporating a business in Canada. It collects the information needed for formation, prepares relevant documents, and provides a dashboard for company records and selected ongoing compliance work.', 'The service is most useful for first-time founders who want guidance without assembling every form themselves. Incorporation requirements, government charges, and included services vary by jurisdiction, so the final checkout details matter more than a single headline price.', 'Ownr is not a law firm and a standardized flow cannot replace tailored legal or tax advice. Businesses with complex ownership, financing, or cross-border structures should consult qualified professionals.'],
    keyFeatures: [{ title: 'Business formation', description: 'Complete a guided flow for eligible sole proprietorship registrations or incorporations.' }, { title: 'Company documents', description: 'Generate and organize core records associated with the selected formation package.' }, { title: 'Ongoing management', description: 'Access reminders and tools for selected corporate maintenance requirements.' }, { title: 'Partner ecosystem', description: 'Connect formation with eligible banking or business-service offers where available.' }],
    alternatives: ['LegalZoom', 'LawDepot', 'Direct incorporation through a registry'],
    notFor: 'Founders outside Canada or companies with complex legal, tax, ownership, or cross-border requirements.',
  },
  {
    slug: 'wati-review',
    name: 'WATI',
    initials: 'W',
    category: 'Marketing',
    tagline: 'Scale conversations on WhatsApp',
    description: 'A WhatsApp Business platform with team inboxes, campaigns, no-code chatbots, and automation designed for customer-facing growth teams.',
    score: 4.5,
    reviews: 2376,
    pricing: 'Platform and usage charges may apply · See current plans',
    bestFor: 'Commerce and support teams with WhatsApp-first customers',
    accent: '#168c62',
    logoBackground: '#dff5e9',
    featured: true,
    partnerUrl: 'https://www.wati.io/',
    pros: ['Purpose-built workflows for WhatsApp Business messaging', 'Shared team inbox and no-code automation', 'Broadcast, template, and contact-management tools'],
    cons: ['Meta conversation charges are separate from platform pricing', 'Setup must follow WhatsApp template and consent policies'],
    verdict: 'WATI is a focused option for teams that rely on WhatsApp for marketing, sales, and customer support conversations.',
    updated: 'August 11, 2026',
    summary: ['WATI is a customer engagement platform built on the WhatsApp Business Platform. It helps multiple agents manage one WhatsApp presence while adding broadcasts, approved message templates, automation, and integrations.', 'The shared inbox is the operational center: conversations can be assigned, labeled, and handled by a team instead of remaining on one employee’s phone. Automation can answer common questions, collect details, or route customers before a human joins.', 'Prospective buyers should model both WATI subscription costs and Meta messaging charges. They should also understand opt-in, template approval, and messaging-window policies before planning campaigns.'],
    keyFeatures: [{ title: 'Team inbox', description: 'Manage WhatsApp conversations collaboratively with assignment, labels, and customer context.' }, { title: 'No-code automation', description: 'Build chatbot flows for qualification, answers, routing, and basic self-service.' }, { title: 'Broadcast campaigns', description: 'Send approved message templates to opted-in audiences and monitor campaign activity.' }, { title: 'Integrations', description: 'Connect WhatsApp workflows with supported commerce, CRM, and automation tools.' }],
    alternatives: ['respond.io', 'Interakt', 'Twilio'],
    notFor: 'Businesses whose customers do not primarily use WhatsApp or teams unwilling to manage Meta compliance requirements.',
  },
  {
    slug: 'notion-review',
    name: 'Notion',
    initials: 'N',
    category: 'Productivity',
    tagline: 'One workspace for docs, projects, and knowledge',
    description: 'A flexible connected workspace that brings notes, databases, projects, and team knowledge together with increasingly capable AI features.',
    score: 4.6,
    reviews: 6814,
    pricing: 'Free and paid plans available · See current plans',
    bestFor: 'Teams that value flexible, connected documentation',
    accent: '#20201f',
    logoBackground: '#efeee8',
    partnerUrl: 'https://www.notion.com/',
    pros: ['Flexible pages and linked databases', 'Strong template and integration ecosystem', 'Docs, projects, and knowledge stay connected'],
    cons: ['Unstructured workspaces can become difficult to govern', 'Offline use is less complete than local-first tools'],
    verdict: 'Notion is an adaptable connected workspace for teams willing to invest in clear structure and governance.',
    updated: 'August 5, 2026',
    summary: ['Notion combines documents, wikis, projects, databases, calendars, and AI-assisted work in a connected workspace. Its block editor lets teams mix text, media, tables, embeds, and structured records on the same page.', 'The flexibility is both the reason to buy and the main risk. A thoughtful workspace can reduce tool switching and make knowledge easier to discover; an unmanaged workspace can create duplicated databases, inconsistent naming, and unclear ownership.', 'Teams get the most value by defining a small set of canonical databases, permissions, and templates before broad adoption.'],
    keyFeatures: [{ title: 'Docs and wikis', description: 'Create collaborative pages and organize verified team knowledge in a searchable workspace.' }, { title: 'Databases', description: 'Represent the same structured information in tables, boards, lists, calendars, and timelines.' }, { title: 'Projects', description: 'Track tasks, owners, dates, dependencies, and project updates alongside documentation.' }, { title: 'Notion AI', description: 'Use integrated AI capabilities to draft, summarize, search, and work with workspace knowledge.' }],
    alternatives: ['Coda', 'Confluence', 'ClickUp'],
    notFor: 'Teams that need strict out-of-the-box processes or a fully local, offline-first knowledge system.',
  },
]

export const articles: EditorialArticle[] = [
  {
    title: 'How we test software: the AIGearBase review framework',
    excerpt: 'A transparent look at the criteria, hands-on checks, and scoring model behind every recommendation we publish.',
    category: 'Inside AIGearBase',
    readTime: '6 min read',
    date: 'Sep 3, 2026',
    accent: '#eadbff',
  },
  {
    title: 'The 7 best sales intelligence platforms for 2026',
    excerpt: 'We compared data quality, workflow depth, integrations, and total cost to find the strongest options for modern revenue teams.',
    category: 'Best software',
    readTime: '12 min read',
    date: 'Aug 29, 2026',
    accent: '#dbeeff',
  },
  {
    title: 'WhatsApp automation: what growing teams need to know',
    excerpt: 'Where automation helps, where it frustrates customers, and how to choose a platform without paying for features you will not use.',
    category: 'Buying guide',
    readTime: '9 min read',
    date: 'Aug 20, 2026',
    accent: '#ddf1e6',
  },
]

export const categories = [
  { name: 'Sales & CRM', count: 38, icon: 'trend' },
  { name: 'AI tools', count: 52, icon: 'sparkles' },
  { name: 'Marketing', count: 44, icon: 'megaphone' },
  { name: 'Productivity', count: 31, icon: 'check' },
  { name: 'Communication', count: 27, icon: 'message' },
  { name: 'Documents', count: 19, icon: 'file' },
]
